// Story 11.5 — Indicador del estado de validación, anclado a la EditorToolbar.

import { CheckCircle2, CircleDashed, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { shouldFadeValid, type ValidationStatus } from '@/lib/validation-state';
import { useValidationStore } from '@/stores/validation-store';

interface Props {
  /** Click handler — típicamente abre el LogPanel filtrado por validation. */
  onClick?: () => void;
}

const STATUS_LABEL: Record<ValidationStatus, string> = {
  idle: 'Sin validar',
  validating: 'Validando…',
  valid: 'Validado',
  invalid: 'Inválido',
};

const STATUS_CLASS: Record<ValidationStatus, string> = {
  idle: 'bg-muted text-muted-foreground border-border',
  validating: 'bg-info/15 text-info border-info/40',
  valid: 'bg-success/15 text-success border-success/40',
  invalid: 'bg-destructive/15 text-destructive border-destructive/40',
};

export function ValidationStatusIndicator({ onClick }: Props): React.ReactElement {
  const status = useValidationStore((s) => s.status);
  const errorCount = useValidationStore((s) => s.errorCount);
  const lastRunAt = useValidationStore((s) => s.lastRunAt);

  // Auto-fade del estado `valid` tras 3s. El render se dispara con un
  // tick que recomputa `shouldFadeValid`.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status !== 'valid' || lastRunAt == null) return;
    const t = window.setTimeout(() => setTick((v) => v + 1), 3000);
    return () => window.clearTimeout(t);
  }, [status, lastRunAt]);

  const faded =
    status === 'valid' &&
    lastRunAt != null &&
    shouldFadeValid({ status, lastRunAt } as never, Date.now());
  void tick; // referenciado para que el linter no marque unused.

  if (faded) {
    // Tras el fade, mostramos el "idle" silenciosamente.
    return <Indicator status="idle" />;
  }

  if (!onClick) return <Indicator status={status} count={errorCount} />;

  return (
    <button
      type="button"
      onClick={onClick}
      title={STATUS_LABEL[status]}
      className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
      aria-label={`${STATUS_LABEL[status]}${errorCount > 0 ? ` (${errorCount} errores)` : ''}`}
    >
      <Indicator status={status} count={errorCount} />
    </button>
  );
}

function Indicator({
  status,
  count = 0,
}: {
  status: ValidationStatus;
  count?: number;
}): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        STATUS_CLASS[status],
      )}
    >
      {status === 'idle' && <CircleDashed className="h-3 w-3" aria-hidden />}
      {status === 'validating' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
      {status === 'valid' && <CheckCircle2 className="h-3 w-3" aria-hidden />}
      {status === 'invalid' && <XCircle className="h-3 w-3" aria-hidden />}
      {STATUS_LABEL[status]}
      {status === 'invalid' && count > 0 && (
        <span className="rounded bg-destructive/30 px-1 text-[10px]">{count}</span>
      )}
    </span>
  );
}
